import React, { useEffect, useMemo, useState } from 'react';
import { TabBar, SkeletonHero, SkeletonCard, SectionError } from '../intel/ui';
import ClubHeader from '../intel/ClubHeader';
import ClubOverview from '../intel/ClubOverview';
import ClubSquad from '../intel/ClubSquad';
import ClubStatistics from '../intel/ClubStatistics';
import ClubFixtures from '../intel/ClubFixtures';
import StandingsTable from '../intel/StandingsTable';
import { buildSquadNameMap } from '../../lib/clubHelpers';

const TABS = ['Overview', 'Squad', 'Statistics', 'Fixtures', 'Standings'];
const EP = (id) => `https://stats.interaios.com/api/v1/teams-sportsmonk/get-team/${id}`;

function extractId(entity) {
  const raw = entity?.raw;
  return raw?.team_id ?? raw?.id ?? raw?.club_id ?? raw?.team?.id ?? null;
}

export default function TeamDashboard({ entity }) {
  const id = extractId(entity);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(EP(id));
        if (!res.ok) throw new Error('Team data unavailable');
        const json = await res.json();
        if (!json?.data?.overview) throw new Error('Team data unavailable');
        if (!cancelled) setData(json.data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, retryKey]);

  const nameMap = useMemo(() => (data ? buildSquadNameMap(data) : {}), [data]);

  return (
    <div className="entity-dashboard intel" style={{ padding: '20px 24px 24px' }}>
      {!id && !loading && (
        <div className="i-card p-5">
          <h3 className="i-fg font-semibold">{entity?.name || 'Team'}</h3>
          <p className="i-dim text-sm mt-1">No team details available for this entity.</p>
        </div>
      )}
      {loading && (<><SkeletonHero />{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</>)}
      {error && <SectionError message={error} onRetry={() => setRetryKey((k) => k + 1)} />}
      {data && !loading && (
        <>
          <ClubHeader data={data} showBack={false} />
          <div className="i-card p-0 mb-4">
            <TabBar tabs={TABS} active={tab} onChange={setTab} />
            <div className="p-4">
              {tab === 'Overview' && <ClubOverview data={data} nameMap={nameMap} />}
              {tab === 'Squad' && <ClubSquad data={data} />}
              {tab === 'Statistics' && <ClubStatistics data={data} nameMap={nameMap} />}
              {tab === 'Fixtures' && <ClubFixtures data={data} />}
              {tab === 'Standings' && (
                data?.standings?.length
                  ? <StandingsTable rows={data.standings} highlightParticipantId={data.overview?.id} />
                  : <p className="i-dim text-sm">No standings available.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
